import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  AccessRequest,
  CreateAccessRequestDto,
  UpdateAccessRequestDto,
  AccessRequestFilters,
} from '../models/access-request.model';
import { ApiResponse } from '../models/common.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AccessRequestsService {
  private readonly baseUrl = `${environment.apiUrl}/device-requests`;

  constructor(private http: HttpClient) {}

  /**
   * Criar um novo pedido de acesso
   * POST /device-requests
   */
  create(request: CreateAccessRequestDto): Observable<ApiResponse<AccessRequest>> {
    return this.http.post<ApiResponse<AccessRequest>>(this.baseUrl, request);
  }

  /**
   * Listar todos os pedidos de acesso
   * GET /device-requests
   */
  getAll(filters?: AccessRequestFilters): Observable<ApiResponse<AccessRequest[]>> {
    let httpParams = new HttpParams();

    if (filters) {
      Object.keys(filters).forEach((key) => {
        if (
          filters[key as keyof AccessRequestFilters] !== null &&
          filters[key as keyof AccessRequestFilters] !== undefined
        ) {
          httpParams = httpParams.set(key, filters[key as keyof AccessRequestFilters] as string);
        }
      });
    }

    return this.http.get<ApiResponse<AccessRequest[]>>(this.baseUrl, { params: httpParams });
  }

  /**
   * Buscar um pedido de acesso por ID
   * GET /device-requests/{id}
   */
  getById(id: string): Observable<ApiResponse<AccessRequest>> {
    return this.http.get<ApiResponse<AccessRequest>>(`${this.baseUrl}/${id}`);
  }

  /**
   * Atualizar um pedido de acesso
   * PATCH /device-requests/{id}
   */
  update(id: string, request: UpdateAccessRequestDto): Observable<ApiResponse<AccessRequest>> {
    return this.http.patch<ApiResponse<AccessRequest>>(`${this.baseUrl}/${id}`, request);
  }

  /**
   * Aprovar um pedido de acesso para um posto (não supervisor)
   * POST /device-requests/{id}/approve
   * @param id ID do device request
   * @param positionId ID do posto (opcional)
   */
  approve(id: string, positionId?: string | number): Observable<ApiResponse<AccessRequest>> {
    const payload: any = {
      is_supervisor: false,
      position_id:positionId
    };
    // if (positionId !== undefined && positionId !== null && positionId !== '') {
    //   // Garantir que position_id seja sempre um número
    //   payload.position_id = typeof positionId === 'string' ? Number(positionId) : positionId;
    // }
    return this.http.post<ApiResponse<AccessRequest>>(`${this.baseUrl}/${id}/approve`, payload);
  }

  /**
   * Aprovar um pedido de acesso como supervisor
   * POST /device-requests/{id}/approve
   */
  approveAsSupervisor(id: string): Observable<ApiResponse<AccessRequest>> {
    return this.http.post<ApiResponse<AccessRequest>>(`${this.baseUrl}/${id}/approve`, {
      is_supervisor: true,
    });
  }

  /**
   * Rejeitar um pedido de acesso
   * POST /device-requests/{id}/reject
   */
  reject(id: string, reason?: string): Observable<ApiResponse<AccessRequest>> {
    const payload: any = {};
    if (reason) {
      payload.reason = reason;
    }
    return this.http.post<ApiResponse<AccessRequest>>(`${this.baseUrl}/${id}/reject`, payload);
  }

  /**
   * Excluir um pedido de acesso
   * DELETE /device-requests/{id}
   */
  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);
  }

  /**
   * Listar todos os pedidos de acesso com resposta completa (inclui status 304)
   * GET /device-requests
   */
  getAllWithResponse(filters?: AccessRequestFilters): Observable<HttpResponse<any>> {
    let httpParams = new HttpParams();

    if (filters) {
      Object.keys(filters).forEach((key) => {
        if (
          filters[key as keyof AccessRequestFilters] !== null &&
          filters[key as keyof AccessRequestFilters] !== undefined
        ) {
          const value = filters[key as keyof AccessRequestFilters] as any;
          httpParams = httpParams.set(key, String(value));
        }
      });
    }

    const headers = new HttpHeaders({
      'Cache-Control': 'no-store',
      Pragma: 'no-cache',
      Expires: '0',
    });

    return this.http.get<any>(this.baseUrl, {
      params: httpParams,
      headers: headers,
      observe: 'response',
    });
  }

  /**
   * Obter estatísticas dos pedidos de acesso
   */
  getStats(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/stats`);
  }
}
